import { z } from 'zod';

/**
 * ADR-6 — membership establishes TENANCY; it does not establish AUTHORIZATION.
 *
 * Seeing a project and being allowed to mutate it are different questions, and
 * the v2 workspace has enough destructive surface (requirements, sourcing,
 * selection, schedule, files, team) that "is a member" is far too coarse a gate.
 *
 * THE ROLE IS THE JOB (owner decision, 2026-09-19). This used to be a generic
 * five — OWNER / ADMIN / EDITOR / CONTRIBUTOR / VIEWER — sitting beside a
 * free-text `production_title` credit that granted nothing, so inviting someone
 * meant answering two questions: what they ARE, and separately what they may
 * DO. Nobody staffing a production thinks that way. They hire a location
 * manager, and a location manager is exactly the person who approves location
 * requirements and confirms a selection. The two answers drifted, and the drift
 * reads as a bug: "why can't the location manager shortlist?"
 *
 * WHY THIS DOES NOT REOPEN THE PRIVILEGE-ESCALATION HOLE ADR 0010 CLOSED.
 * That ADR's objection was to reading permission off a FREE-TEXT field — anyone
 * who could type "Producer" into their own credit would have granted it to
 * themselves. This is a CLOSED catalogue, assigned by someone holding
 * `TEAM_MANAGE`, exactly as the old role dropdown was. `production_title`
 * survives unchanged as the credit line — "2nd Unit", "Additional Photography,
 * Sofia" — still free text and still granting nothing, so a production can
 * spell a credit however it likes without touching access. Note the example is
 * deliberately NOT a job title: "Supervising Location Manager" is a role in the
 * catalogue below, and offering it as a credit would invite somebody to type
 * their role into the field that grants nothing and wonder why it did.
 */

/**
 * What a member may do.
 *
 * ONE PERMISSION PER GRID CELL. `PERMISSION_GRID` below lays these out as
 * features × actions, which is the shape a producer configures them in, and the
 * mapping is one-to-one in both directions — a permission that appears in no
 * cell is unreachable from the editor, and a cell with no permission is a
 * checkbox that does nothing. `contracts.test.ts` asserts both.
 *
 * READING IS PER FEATURE. `PROJECT_READ` is the membership gate — can you open
 * this project at all — and is not revocable. The nine `READ_*` permissions
 * beside it are: every role holds all of them by default (ADR 0014's "everyone
 * on a production sees the production" is the DEFAULT, not the law), and a
 * project can close one without touching the others.
 *
 * REMOVING IS SEPARATE FROM DOING. `REQUIREMENT_DISMISS`, `CANDIDATE_REJECT`,
 * `REFERENCE_REMOVE` and `INVITATION_REVOKE` used to be the same permission as
 * their create/approve sibling, so "a scout may add candidates but not reject
 * them" was inexpressible. They are implied by that sibling by
 * default (see `IMPLIED_PERMISSIONS`), so nothing changed at the cutover, and a
 * project can now revoke just the removal.
 *
 * NOTHING IS DESTROYED BY ANY OF THEM (ADR 0011). "Remove" here means dismiss,
 * reject, revoke or archive — a state change with a tombstone. There are two
 * `@Delete` routes in this service and both soft-delete.
 */
export const ProjectPermission = z.enum([
  // Project
  'PROJECT_READ',
  'PROJECT_EDIT',
  // Team
  'READ_TEAM',
  'TEAM_MANAGE',
  'INVITATION_REVOKE',
  // References
  'READ_REFERENCES',
  'REFERENCE_EDIT',
  'REFERENCE_REMOVE',
  // Script & scenes
  'READ_SCRIPT',
  'SCRIPT_UPLOAD',
  'SCRIPT_REVIEW',
  'AI_RUN',
  // Requirements
  'READ_REQUIREMENTS',
  'REQUIREMENT_APPROVE',
  'SOURCING_ACTIVATE',
  'REQUIREMENT_DISMISS',
  // Locations
  'READ_LOCATIONS',
  'CANDIDATE_EDIT',
  'SELECTION_CONFIRM',
  'CANDIDATE_REJECT',
  // Schedule
  'READ_SCHEDULE',
  'SCHEDULE_EDIT',
  // Requests
  'READ_REQUESTS',
  'REQUEST_CREATE',
  'REQUEST_MANAGE',
  // Files
  'READ_FILES',
  'FILE_UPLOAD',
  'FILE_MANAGE',
  // Conversations (ADR 0021)
  'READ_CONVERSATIONS',
  'CONVERSATION_POST',
  'CONVERSATION_MANAGE',
  'CONVERSATION_MODERATE',
]);
export type ProjectPermission = z.infer<typeof ProjectPermission>;

/** The rows of the editor: a domain a producer recognises, not a controller. */
export const PermissionFeature = z.enum([
  'PROJECT',
  'TEAM',
  'REFERENCES',
  'SCRIPT',
  'REQUIREMENTS',
  'LOCATIONS',
  'SCHEDULE',
  'REQUESTS',
  'FILES',
  'CONVERSATIONS',
]);
export type PermissionFeature = z.infer<typeof PermissionFeature>;

/**
 * The columns.
 *
 * `DECIDE` is not a politer word for `WORK`. It is where the acts that spend
 * money or commit the production live — starting a search, confirming a
 * location, running the model — and keeping them in their own column is what
 * stops a producer handing them out while ticking "can edit".
 */
export const PermissionAction = z.enum(['READ', 'WORK', 'DECIDE', 'REMOVE']);
export type PermissionAction = z.infer<typeof PermissionAction>;

export interface PermissionCell {
  readonly feature: PermissionFeature;
  readonly action: PermissionAction;
  readonly permission: ProjectPermission;
  /** What a producer reads in the cell. */
  readonly label: string;
  /** Spends real money — the model budget, or the production's own. */
  readonly spends?: boolean;
  /** Cannot be revoked by anyone. */
  readonly locked?: boolean;
}

export const PERMISSION_FEATURE_LABELS: Readonly<Record<PermissionFeature, string>> = Object.freeze({
  PROJECT: 'Project & billing',
  TEAM: 'Team',
  REFERENCES: 'References',
  SCRIPT: 'Script & scenes',
  REQUIREMENTS: 'Requirements',
  LOCATIONS: 'Locations',
  SCHEDULE: 'Schedule',
  REQUESTS: 'Requests',
  FILES: 'Files',
  CONVERSATIONS: 'Conversations',
});

export const ACTION_LABELS: Readonly<Record<PermissionAction, string>> = Object.freeze({
  READ: 'See',
  WORK: 'Work',
  DECIDE: 'Decide',
  REMOVE: 'Remove',
});

/**
 * EVERY CELL THE EDITOR CAN DRAW, and nothing else exists.
 *
 * A feature may hold more than one permission in a column — approving a
 * requirement and starting a search are both decisions and are not the same
 * decision — so the editor stacks them rather than collapsing them into one
 * checkbox. An empty (feature, action) pair renders as "—", never as an
 * unticked box: an empty box invites a producer to tick it and wonder why
 * nothing happened.
 */
export const PERMISSION_GRID: readonly PermissionCell[] = Object.freeze([
  // Project ---------------------------------------------------------------
  {
    feature: 'PROJECT',
    action: 'READ',
    permission: 'PROJECT_READ',
    label: 'Open the project',
    // This IS membership. Revoking it would leave somebody on the team list
    // who cannot load the workspace, which reads as an outage rather than as a
    // permission — remove them from the team instead.
    locked: true,
  },
  {
    feature: 'PROJECT',
    action: 'DECIDE',
    permission: 'PROJECT_EDIT',
    label: 'Rename, re-phase, archive; billing',
  },

  // Team ------------------------------------------------------------------
  { feature: 'TEAM', action: 'READ', permission: 'READ_TEAM', label: 'See who is on it' },
  { feature: 'TEAM', action: 'WORK', permission: 'TEAM_MANAGE', label: 'Invite and change roles' },
  {
    feature: 'TEAM',
    action: 'REMOVE',
    permission: 'INVITATION_REVOKE',
    label: 'Revoke an invitation',
  },

  // References ------------------------------------------------------------
  {
    feature: 'REFERENCES',
    action: 'READ',
    permission: 'READ_REFERENCES',
    label: 'See the board',
  },
  {
    feature: 'REFERENCES',
    action: 'WORK',
    permission: 'REFERENCE_EDIT',
    label: 'Add and annotate',
  },
  {
    feature: 'REFERENCES',
    action: 'REMOVE',
    permission: 'REFERENCE_REMOVE',
    label: 'Take one off the board',
  },

  // Script & scenes -------------------------------------------------------
  { feature: 'SCRIPT', action: 'READ', permission: 'READ_SCRIPT', label: 'Read the screenplay' },
  { feature: 'SCRIPT', action: 'WORK', permission: 'SCRIPT_UPLOAD', label: 'Upload a draft' },
  { feature: 'SCRIPT', action: 'WORK', permission: 'SCRIPT_REVIEW', label: 'Work the breakdown' },
  {
    feature: 'SCRIPT',
    action: 'DECIDE',
    permission: 'AI_RUN',
    label: 'Run the AI analysis',
    spends: true,
  },

  // Requirements ----------------------------------------------------------
  {
    feature: 'REQUIREMENTS',
    action: 'READ',
    permission: 'READ_REQUIREMENTS',
    label: 'See the location list',
  },
  {
    feature: 'REQUIREMENTS',
    action: 'DECIDE',
    permission: 'REQUIREMENT_APPROVE',
    label: 'Approve, merge, split',
  },
  {
    feature: 'REQUIREMENTS',
    action: 'DECIDE',
    permission: 'SOURCING_ACTIVATE',
    label: 'Start sourcing',
    spends: true,
  },
  {
    feature: 'REQUIREMENTS',
    action: 'REMOVE',
    permission: 'REQUIREMENT_DISMISS',
    label: 'Dismiss a requirement',
  },

  // Locations -------------------------------------------------------------
  {
    feature: 'LOCATIONS',
    action: 'READ',
    permission: 'READ_LOCATIONS',
    label: 'See matches and the shortlist',
  },
  {
    feature: 'LOCATIONS',
    action: 'WORK',
    permission: 'CANDIDATE_EDIT',
    label: 'Add and shortlist candidates',
  },
  {
    feature: 'LOCATIONS',
    action: 'DECIDE',
    permission: 'SELECTION_CONFIRM',
    label: 'Confirm the location',
  },
  {
    feature: 'LOCATIONS',
    action: 'REMOVE',
    permission: 'CANDIDATE_REJECT',
    label: 'Reject a candidate',
  },

  // Schedule --------------------------------------------------------------
  { feature: 'SCHEDULE', action: 'READ', permission: 'READ_SCHEDULE', label: 'See the board' },
  {
    feature: 'SCHEDULE',
    action: 'WORK',
    permission: 'SCHEDULE_EDIT',
    label: 'Shooting days, stripboard, DOOD',
  },

  // Requests --------------------------------------------------------------
  { feature: 'REQUESTS', action: 'READ', permission: 'READ_REQUESTS', label: 'See them' },
  { feature: 'REQUESTS', action: 'WORK', permission: 'REQUEST_CREATE', label: 'Raise one' },
  {
    feature: 'REQUESTS',
    action: 'DECIDE',
    permission: 'REQUEST_MANAGE',
    label: 'Answer and close them',
  },

  // Files -----------------------------------------------------------------
  { feature: 'FILES', action: 'READ', permission: 'READ_FILES', label: 'See and download' },
  { feature: 'FILES', action: 'WORK', permission: 'FILE_UPLOAD', label: 'Upload' },
  { feature: 'FILES', action: 'DECIDE', permission: 'FILE_MANAGE', label: 'Rename and re-file' },
  // No Files/REMOVE cell: this service has no route that takes a file down.
  // A checkbox governing nothing is worse than an honest "—", and
  // `permission-grid.test.ts` fails the build if a cell is added without one.

  // Conversations (ADR 0021) ---------------------------------------------
  // Reading a thread also needs its anchor's own feature read — a scene thread
  // is closed to somebody this production keeps out of the script — and a
  // private thread needs active participation (`canReadThread`, conversations.ts).
  {
    feature: 'CONVERSATIONS',
    action: 'READ',
    permission: 'READ_CONVERSATIONS',
    label: 'Read the team\'s conversations',
  },
  {
    feature: 'CONVERSATIONS',
    action: 'WORK',
    permission: 'CONVERSATION_POST',
    label: 'Start and reply; manage your private threads',
  },
  {
    feature: 'CONVERSATIONS',
    action: 'DECIDE',
    permission: 'CONVERSATION_MANAGE',
    label: 'Rename, resolve or reopen any public thread',
  },
  {
    feature: 'CONVERSATIONS',
    action: 'REMOVE',
    permission: 'CONVERSATION_MODERATE',
    label: 'Hide someone else\'s message',
  },
]);

/**
 * The nine REVOCABLE reads, which every role holds unless a project closes one.
 *
 * `PROJECT_READ` is deliberately not among them: it is the locked membership
 * gate, not a feature, and folding it in here would make "every role holds
 * every feature read" a statement that quietly includes the one thing no
 * project may take away.
 */
export const FEATURE_READ_PERMISSIONS: readonly ProjectPermission[] = Object.freeze(
  PERMISSION_GRID.filter((c) => c.action === 'READ' && c.locked !== true).map((c) => c.permission),
);

/**
 * A permission that comes free with another one.
 *
 * These five were the SAME permission as their sibling until the grid needed a
 * Remove column: revoking an invitation was `TEAM_MANAGE`, rejecting a
 * candidate was `CANDIDATE_EDIT`. Splitting them made "may add candidates, may
 * not reject them" expressible; implying them keeps every existing role exactly
 * as capable as it was, so the split cost nobody anything.
 */
export const IMPLIED_PERMISSIONS: Readonly<Record<string, readonly ProjectPermission[]>> =
  Object.freeze({
    TEAM_MANAGE: ['INVITATION_REVOKE'],
    PROJECT_EDIT: ['REFERENCE_EDIT', 'REFERENCE_REMOVE'],
    REQUIREMENT_APPROVE: ['REQUIREMENT_DISMISS'],
    CANDIDATE_EDIT: ['CANDIDATE_REJECT'],
  });

/** Where a role sits on a call sheet. Display grouping only — grants nothing. */
export const RoleDepartment = z.enum([
  'PRODUCTION',
  'DIRECTING',
  'WRITING',
  'LOCATIONS',
  'DESIGN_AND_CAMERA',
  'SUPPORT',
]);
export type RoleDepartment = z.infer<typeof RoleDepartment>;

interface RoleDefinition {
  /** What a person reads — "Location Manager", never `LOCATION_MANAGER`. */
  readonly label: string;
  readonly department: RoleDepartment;
  /** One line, shown beside the role in the invite picker. Says what they may
   *  CHANGE, because reading is never the question. */
  readonly summary: string;
  /** `PROJECT_READ` is added for every role by `PERMISSIONS_BY_ROLE` below and
   *  is deliberately not repeated here — a role that forgot it would be
   *  invisible rather than restricted. */
  readonly grants: readonly ProjectPermission[];
}

/** Everything that is not a read. A role granted these can do anything. */
const ALL_WRITES: readonly ProjectPermission[] = ProjectPermission.options.filter(
  (p) => p !== 'PROJECT_READ' && !FEATURE_READ_PERMISSIONS.includes(p),
);

/**
 * HIDING SOMEBODY ELSE'S WORDS IS NARROWER THAN RUNNING THE PRODUCTION (owner
 * review of 2026-10-28). `CONVERSATION_MODERATE` defaults to the three roles that
 * answer for the whole production — Pro Co Owner, Executive Producer, Producer —
 * and is taken back out of every other role built from `ALL_WRITES`, which keep
 * `CONVERSATION_MANAGE` (rename, resolve, reopen a thread). A production can
 * grant it further on the grid.
 */
const withoutModeration = (permissions: readonly ProjectPermission[]): ProjectPermission[] =>
  permissions.filter((p) => p !== 'CONVERSATION_MODERATE');

/**
 * THE CATALOGUE. Adding a role is ONE entry here and nothing else:
 * `authorization_role` is `varchar(64)` with no enum and no CHECK constraint,
 * so there is no migration, no DDL and no API change. The Zod enum, the labels,
 * the permission matrix and the invite picker all derive from this object.
 *
 * Roles may legitimately share a permission set — an art director and a DOP
 * need the same access and are not the same job, and the team list is the
 * poorer for collapsing them. What must never happen is two entries with the
 * same KEY, which `contracts.test.ts` asserts.
 */
export const ROLE_CATALOGUE = Object.freeze({
  // ---- Production --------------------------------------------------------
  PRO_CO_OWNER: {
    label: 'Pro Co Owner',
    department: 'PRODUCTION',
    summary: 'Everything, including the project record and the team.',
    grants: ALL_WRITES,
  },
  EXECUTIVE_PRODUCER: {
    label: 'Executive Producer',
    department: 'PRODUCTION',
    summary: 'Everything except renaming, re-phasing or archiving the project.',
    grants: ALL_WRITES.filter((p) => p !== 'PROJECT_EDIT'),
  },
  PRODUCER: {
    label: 'Producer',
    department: 'PRODUCTION',
    summary: 'Everything, including the project record and the team.',
    grants: ALL_WRITES,
  },
  HEAD_OF_PRODUCTION: {
    label: 'Head of Production',
    department: 'PRODUCTION',
    summary: 'Runs the production department and its team; not the project record itself.',
    grants: withoutModeration(ALL_WRITES.filter((p) => p !== 'PROJECT_EDIT')),
  },
  LINE_PRODUCER: {
    label: 'Line Producer',
    department: 'PRODUCTION',
    summary: 'Runs the production day to day; cannot change the project record or the team.',
    grants: withoutModeration(
      ALL_WRITES.filter((p) => p !== 'PROJECT_EDIT' && p !== 'TEAM_MANAGE'),
    ),
  },
  UNIT_PRODUCTION_MANAGER: {
    label: 'Unit Production Manager',
    department: 'PRODUCTION',
    summary: 'The schedule and the money: sourcing, selection, requests and files.',
    grants: [
      'SCRIPT_UPLOAD',
      'SCRIPT_REVIEW',
      'REQUIREMENT_APPROVE',
      'SOURCING_ACTIVATE',
      'CANDIDATE_EDIT',
      'SELECTION_CONFIRM',
      'SCHEDULE_EDIT',
      'REQUEST_CREATE',
      'REQUEST_MANAGE',
      'FILE_UPLOAD',
      'FILE_MANAGE',
      'AI_RUN',
      'CONVERSATION_POST',
    ],
  },
  PRODUCTION_MANAGER: {
    label: 'Production Manager',
    department: 'PRODUCTION',
    summary: 'Sourcing and scheduling; the final selection stays with the UPM.',
    grants: [
      'SCRIPT_REVIEW',
      'REQUIREMENT_APPROVE',
      'SOURCING_ACTIVATE',
      'CANDIDATE_EDIT',
      'SCHEDULE_EDIT',
      'REQUEST_CREATE',
      'FILE_UPLOAD',
      'FILE_MANAGE',
      'AI_RUN',
      'CONVERSATION_POST',
    ],
  },
  PRODUCTION_COORDINATOR: {
    label: 'Production Coordinator',
    department: 'PRODUCTION',
    summary: 'Keeps the schedule, the requests and the paperwork moving.',
    grants: ['SCRIPT_REVIEW', 'SCHEDULE_EDIT', 'REQUEST_CREATE', 'FILE_UPLOAD', 'FILE_MANAGE', 'CONVERSATION_POST'],
  },
  PRODUCTION_ACCOUNTANT: {
    label: 'Production Accountant',
    department: 'PRODUCTION',
    summary: 'Reads everything and files paperwork; changes nothing creative or operational.',
    grants: ['FILE_UPLOAD', 'CONVERSATION_POST'],
  },
  PRODUCTION_ASSISTANT: {
    label: 'Production Assistant',
    department: 'PRODUCTION',
    summary: 'Raises requests and uploads files.',
    grants: ['REQUEST_CREATE', 'FILE_UPLOAD', 'CONVERSATION_POST'],
  },

  // ---- Directing ---------------------------------------------------------
  DIRECTOR: {
    label: 'Director',
    department: 'DIRECTING',
    summary: 'Creative approval: what a location must be, and which candidates are right.',
    // NOT `SELECTION_CONFIRM`, and that is a decision rather than an omission:
    // confirming a selection is what commits the production to a place and is
    // paired with booking it. The director chooses; the location manager books.
    // One line here if a production wants it the other way.
    grants: ['SCRIPT_REVIEW', 'REQUIREMENT_APPROVE', 'CANDIDATE_EDIT', 'CONVERSATION_POST'],
  },
  CREATIVE_DIRECTOR: {
    label: 'Creative Director',
    department: 'DIRECTING',
    // The agency or client-side creative authority on a commercial. Judges the
    // look and signs off what a location must be; the shoot itself is somebody
    // else's to schedule and commit.
    summary: 'Creative approval on what a location must be; commits to none of them.',
    grants: ['SCRIPT_REVIEW', 'REQUIREMENT_APPROVE', 'CANDIDATE_EDIT', 'CONVERSATION_POST'],
  },
  FIRST_ASSISTANT_DIRECTOR: {
    label: '1st Assistant Director',
    department: 'DIRECTING',
    summary: 'Owns the board: shooting days, scene assignments and Day Out of Days.',
    grants: ['SCRIPT_REVIEW', 'SCHEDULE_EDIT', 'REQUEST_CREATE', 'CONVERSATION_POST'],
  },
  SECOND_ASSISTANT_DIRECTOR: {
    label: '2nd Assistant Director',
    department: 'DIRECTING',
    summary: 'Works the breakdown and raises requests; does not move the board.',
    grants: ['SCRIPT_REVIEW', 'REQUEST_CREATE', 'CONVERSATION_POST'],
  },
  SCRIPT_SUPERVISOR: {
    label: 'Script Supervisor',
    department: 'DIRECTING',
    summary: 'Works the scene breakdown; changes nothing outside it.',
    grants: ['SCRIPT_REVIEW', 'CONVERSATION_POST'],
  },

  // ---- Writing -----------------------------------------------------------
  SHOWRUNNER: {
    label: 'Showrunner',
    department: 'WRITING',
    // Head writer AND the show's operational authority — the one job where the
    // creative and the practical are the same person's, which is why this is
    // the only writing role that reaches the schedule and the team.
    summary: 'Runs the show: the scripts, the locations, the board and the team.',
    grants: withoutModeration(ALL_WRITES.filter((p) => p !== 'PROJECT_EDIT')),
  },
  WRITER_CREATOR: {
    label: 'Writer-Creator',
    department: 'WRITING',
    summary: 'Owns the material and what a location must be; not the search or the schedule.',
    grants: ['SCRIPT_UPLOAD', 'SCRIPT_REVIEW', 'REQUIREMENT_APPROVE', 'CANDIDATE_EDIT', 'AI_RUN', 'CONVERSATION_POST'],
  },
  SCREENWRITER: {
    label: 'Screenwriter',
    department: 'WRITING',
    summary: 'Uploads drafts and runs the breakdown on them; approves no locations.',
    grants: ['SCRIPT_UPLOAD', 'SCRIPT_REVIEW', 'AI_RUN', 'CONVERSATION_POST'],
  },

  // ---- Locations ---------------------------------------------------------
  SUPERVISING_LOCATION_MANAGER: {
    label: 'Supervising Location Manager',
    department: 'LOCATIONS',
    summary: 'Runs the location department across units, including its paperwork.',
    grants: [
      'SCRIPT_REVIEW',
      'REQUIREMENT_APPROVE',
      'SOURCING_ACTIVATE',
      'CANDIDATE_EDIT',
      'SELECTION_CONFIRM',
      'REQUEST_CREATE',
      'REQUEST_MANAGE',
      'FILE_UPLOAD',
      'FILE_MANAGE',
      'AI_RUN',
      'CONVERSATION_POST',
    ],
  },
  LOCATION_MANAGER: {
    label: 'Location Manager',
    department: 'LOCATIONS',
    summary: 'Owns sourcing end to end: requirements, search, shortlist and the final selection.',
    grants: [
      'SCRIPT_REVIEW',
      'REQUIREMENT_APPROVE',
      'SOURCING_ACTIVATE',
      'CANDIDATE_EDIT',
      'SELECTION_CONFIRM',
      'REQUEST_CREATE',
      'REQUEST_MANAGE',
      'FILE_UPLOAD',
      'AI_RUN',
      'CONVERSATION_POST',
    ],
  },
  ASSISTANT_LOCATION_MANAGER: {
    label: 'Assistant Location Manager',
    department: 'LOCATIONS',
    summary: 'Adds and works candidates; cannot start a search or confirm a location.',
    grants: ['SCRIPT_REVIEW', 'CANDIDATE_EDIT', 'REQUEST_CREATE', 'FILE_UPLOAD', 'CONVERSATION_POST'],
  },
  LOCATION_SCOUT: {
    label: 'Location Scout',
    department: 'LOCATIONS',
    summary: 'Brings places in and photographs them; decides nothing.',
    grants: ['SCRIPT_REVIEW', 'CANDIDATE_EDIT', 'FILE_UPLOAD', 'CONVERSATION_POST'],
  },

  // ---- Design and camera -------------------------------------------------
  PRODUCTION_DESIGNER: {
    label: 'Production Designer',
    department: 'DESIGN_AND_CAMERA',
    summary: 'Creative approval on what a location must look like.',
    grants: ['SCRIPT_REVIEW', 'REQUIREMENT_APPROVE', 'CANDIDATE_EDIT', 'CONVERSATION_POST'],
  },
  ART_DIRECTOR: {
    label: 'Art Director',
    department: 'DESIGN_AND_CAMERA',
    summary: 'Works candidates against the design; approves no requirements.',
    grants: ['SCRIPT_REVIEW', 'CANDIDATE_EDIT', 'CONVERSATION_POST'],
  },
  DIRECTOR_OF_PHOTOGRAPHY: {
    label: 'Director of Photography',
    department: 'DESIGN_AND_CAMERA',
    summary: 'Judges candidates on light and space; approves no requirements.',
    grants: ['SCRIPT_REVIEW', 'CANDIDATE_EDIT', 'CONVERSATION_POST'],
  },

  // ---- Support -----------------------------------------------------------
  PRODUCTION_SUPERVISOR: {
    label: 'Production Supervisor',
    department: 'SUPPORT',
    summary: 'Runs the board, the requests and the paperwork; approves no locations.',
    grants: [
      'SCRIPT_REVIEW',
      'SCHEDULE_EDIT',
      'REQUEST_CREATE',
      'REQUEST_MANAGE',
      'FILE_UPLOAD',
      'FILE_MANAGE',
      'CONVERSATION_POST',
    ],
  },
  LEGAL_AND_CLEARANCES: {
    label: 'Legal & Clearances',
    department: 'SUPPORT',
    summary: 'Owns permits, agreements and releases; touches no creative decision.',
    grants: ['SCRIPT_REVIEW', 'FILE_UPLOAD', 'FILE_MANAGE', 'CONVERSATION_POST'],
  },
  OBSERVER: {
    label: 'Observer',
    department: 'SUPPORT',
    // The financier, the studio executive, the completion bond. They see the
    // production and change nothing — which is a real job, and the reason
    // `PROJECT_READ` had to exist: the old VIEWER held no permissions at all
    // and every read door was gated on one, so it could see nothing.
    summary: 'Reads everything and changes nothing.',
    grants: [],
  },
} as const satisfies Record<string, RoleDefinition>);

export type AuthorizationRole = keyof typeof ROLE_CATALOGUE;

/** Every role, in catalogue order — which is call-sheet order, not alphabetical. */
export const AUTHORIZATION_ROLES: readonly AuthorizationRole[] = Object.freeze(
  Object.keys(ROLE_CATALOGUE) as AuthorizationRole[],
);

/**
 * Derived from the catalogue, so the wire contract cannot fall behind it. The
 * tuple assertion is what `z.enum` needs and what `Object.keys` cannot prove;
 * `contracts.test.ts` asserts the two agree in both directions.
 */
export const AuthorizationRole = z.enum(
  AUTHORIZATION_ROLES as [AuthorizationRole, ...AuthorizationRole[]],
);

/** A grant plus everything it implies, transitively closed in one pass. */
function withImplied(granted: readonly ProjectPermission[]): ProjectPermission[] {
  const out = new Set<ProjectPermission>(granted);
  for (const permission of granted) {
    for (const implied of IMPLIED_PERMISSIONS[permission] ?? []) out.add(implied);
  }
  return [...out];
}

/**
 * THE DEFAULT for each role — what it can do in a project that has never
 * touched its permissions.
 *
 * Derived rather than written out on three counts, each of which is a mistake
 * somebody would otherwise make once: `PROJECT_READ` and every `READ_*` are
 * added to all of them, so a role cannot ship able to do things and unable to
 * see them; the implied removals are added, so splitting "reject" out of
 * "shortlist" cost no existing role anything; and the order follows
 * `ProjectPermission.options`, so two roles with the same permissions compare
 * equal however their catalogue entries were written.
 */
/**
 * The matrix as a lookup by ARBITRARY string.
 *
 * `authorization_role` is a varchar, so a role this build has never heard of can
 * reach these functions. Indexing the matrix by `AuthorizationRole` would type
 * the miss away and leave the `undefined` guards looking redundant; this view
 * keeps them honest.
 */
type PermissionLookup = Readonly<Record<string, readonly ProjectPermission[] | undefined>>;

export const PERMISSIONS_BY_ROLE: Readonly<
  Record<AuthorizationRole, readonly ProjectPermission[]>
> = Object.freeze(
  Object.fromEntries(
    AUTHORIZATION_ROLES.map((role) => {
      const granted = new Set(
        withImplied([
          'PROJECT_READ',
          ...FEATURE_READ_PERMISSIONS,
          ...ROLE_CATALOGUE[role].grants,
        ]),
      );

      return [role, Object.freeze(ProjectPermission.options.filter((p) => granted.has(p)))];
    }),
  ) as Record<AuthorizationRole, readonly ProjectPermission[]>,
);

/** What a project has said about one of its roles, cell by cell. */
export const PermissionOverrideState = z.enum(['GRANTED', 'REVOKED', 'DEFAULT']);
export type PermissionOverrideState = z.infer<typeof PermissionOverrideState>;

export interface PermissionOverride {
  readonly permission: ProjectPermission;
  readonly state: PermissionOverrideState;
}

/**
 * WHAT A ROLE MAY ACTUALLY DO IN ONE PROJECT.
 *
 * The catalogue is the default and the project is allowed to disagree in both
 * directions — a production that wants its scout confirming locations, or its
 * UPM kept off the board, says so here rather than inventing a role.
 *
 * `PROJECT_READ` SURVIVES EVERYTHING. It is membership itself: a member who
 * cannot open the project is not a restricted member, they are a bug report.
 * Removing somebody from the team is the way to take that away.
 *
 * An override naming a permission this build has never heard of is IGNORED
 * rather than trusted — the column is a varchar and a newer deploy can write
 * into it, and "grant something I cannot name" is the one interpretation that
 * must never win.
 */
export function effectivePermissions(
  role: string,
  overrides: readonly PermissionOverride[] = [],
): readonly ProjectPermission[] {
  // Looked up through a widened view rather than `role as AuthorizationRole`:
  // the cast would claim the key is always one this build knows, which is the
  // very thing that is not true here, and would type the miss out of existence.
  const base = (PERMISSIONS_BY_ROLE as PermissionLookup)[role];
  if (base === undefined) return [];

  const effective = new Set<ProjectPermission>(base);

  for (const override of overrides) {
    if (!ProjectPermission.options.includes(override.permission)) continue;
    if (override.state === 'GRANTED') effective.add(override.permission);
    if (override.state === 'REVOKED') effective.delete(override.permission);
  }

  effective.add('PROJECT_READ');

  return ProjectPermission.options.filter((p) => effective.has(p));
}

/** The cell for a permission, or undefined if it is not on the grid. */
export function cellFor(permission: string): PermissionCell | undefined {
  return PERMISSION_GRID.find((c) => c.permission === permission);
}

/** Can a project change this cell at all? */
export function isLocked(permission: string): boolean {
  return cellFor(permission)?.locked === true;
}

/**
 * The role as a person reads it.
 *
 * Shared rather than restated per surface because it leaves this service: the
 * invitation email renders it in Laravel, and a recipient told they are joining
 * as `ASSISTANT_LOCATION_MANAGER` has been shown a database value, not a job.
 */
export const AUTHORIZATION_ROLE_LABELS: Readonly<Record<AuthorizationRole, string>> =
  Object.freeze(
    Object.fromEntries(AUTHORIZATION_ROLES.map((role) => [role, ROLE_CATALOGUE[role].label])) as
      Record<AuthorizationRole, string>,
  );

export const ROLE_DEPARTMENT_LABELS: Readonly<Record<RoleDepartment, string>> = Object.freeze({
  PRODUCTION: 'Production',
  DIRECTING: 'Directing',
  WRITING: 'Writing',
  LOCATIONS: 'Locations',
  DESIGN_AND_CAMERA: 'Design & camera',
  SUPPORT: 'Support',
});

/**
 * UNKNOWN ROLE MEANS NO PERMISSIONS, NEVER A CRASH.
 *
 * `authorization_role` is a `varchar`, so the database can hold a value this
 * build has never heard of — a row written by a newer deploy during a rollout,
 * or a legacy value a migration missed. Indexing the matrix directly returns
 * `undefined` and `undefined.includes(...)` is a 500 on a permission check,
 * which fails OPEN in the worst way: the request errors instead of being
 * refused, and whoever reads the log sees a bug rather than a denial.
 */
export function roleHasPermission(role: string, permission: ProjectPermission): boolean {
  const granted = (PERMISSIONS_BY_ROLE as PermissionLookup)[role];

  return granted?.includes(permission) ?? false;
}

export function isAuthorizationRole(value: string): value is AuthorizationRole {
  return Object.prototype.hasOwnProperty.call(ROLE_CATALOGUE, value);
}

/**
 * The generic roles this catalogue replaced, and what each became.
 *
 * KEPT AFTER THE MIGRATION HAS RUN, on purpose. An SPA cached in a producer's
 * browser goes on POSTing `EDITOR` for as long as that tab is open, and an
 * invitation email sent before the deploy is accepted after it. Rejecting those
 * with a 422 would make a mid-deploy invite unredeemable for no reason.
 *
 * EVERY MAPPING IS TO A SUPERSET. Nobody's access narrowed at the cutover:
 * `VIEWER` is the only one that moved at all, and it GAINED the read it was
 * always supposed to have. `scripts/` has no rollback for a widening, so the
 * direction matters.
 */
export const LEGACY_ROLE_ALIASES: Readonly<Record<string, AuthorizationRole>> = Object.freeze({
  OWNER: 'PRODUCER',
  // NOT `LINE_PRODUCER`, which is the closer job title: the legacy ADMIN held
  // `TEAM_MANAGE` and a line producer does not, so that mapping would have
  // taken the team away from whoever was running it. The superset rule wins
  // over the nicer-sounding name.
  ADMIN: 'EXECUTIVE_PRODUCER',
  EDITOR: 'UNIT_PRODUCTION_MANAGER',
  CONTRIBUTOR: 'ASSISTANT_LOCATION_MANAGER',
  VIEWER: 'OBSERVER',
});

/**
 * Read a role off the wire or out of a row, tolerating the generic five.
 *
 * Returns null for anything else — an unknown string is not coerced to a
 * default, because a typo that silently became `OBSERVER` would look like a
 * permissions bug and a typo that silently became `PRODUCER` would be an
 * incident.
 */
export function normaliseRole(value: string): AuthorizationRole | null {
  if (isAuthorizationRole(value)) return value;
  return LEGACY_ROLE_ALIASES[value] ?? null;
}
